// utils.js - CORRECTED to only clean metadata.id
const getNumericMetadataId = (existingId) => {
  if (existingId) {
    const match = String(existingId).match(/^(\d+)/);
    if (match && match[1]) {
      return match[1]; // Return only the initial numeric part
    }
  }
  return `${Date.now()}`; // Fallback
};

// This function is for generating NEW IDs for points/metadata that don't have one.
export const generateId = () => Math.random().toString(36).substring(2, 15);

export const DEFAULT_MAP_DISPLAY_SETTINGS = {
  popup_stop: {
    visibility: true,
    label: true,
  },
  stop: {
    visibility: true,
    label: true,
  },
};

const normalizeMapDisplayRule = (rule, fallback) => ({
  visibility: rule?.visibility !== undefined ? Boolean(rule.visibility) : fallback.visibility,
  label: rule?.label !== undefined ? Boolean(rule.label) : fallback.label,
});

export const normalizeMapDisplaySettings = (mapDisplay) => ({
  popup_stop: normalizeMapDisplayRule(mapDisplay?.popup_stop, DEFAULT_MAP_DISPLAY_SETTINGS.popup_stop),
  stop: normalizeMapDisplayRule(mapDisplay?.stop, DEFAULT_MAP_DISPLAY_SETTINGS.stop),
});

// Generate proper POI ID following the enforced format: {guide_id}_{stop_sequence}_{language_code}
export const generatePoiId = (metadata, existingPoints = []) => {
  if (!metadata) {
    console.warn('No metadata provided for POI ID generation, falling back to random ID');
    return generateId();
  }

  // Get the guide identifier (parent_guide_id takes priority over metadata.id)
  const guideId = metadata.parent_guide_id || metadata.id;

  if (!guideId) {
    console.warn('No guide ID found in metadata, falling back to random ID');
    return generateId();
  }

  // Extract only the first 2 characters for language code (e.g., "pt-pt" -> "pt", "en-US" -> "en")
  const fullLanguage = String(metadata.language || 'en').toLowerCase();
  const language = fullLanguage.split(/[-_]/)[0];

  // Generate next sequence number based on existing points
  const nextSequence = existingPoints.length + 1;

  return `${guideId}_${nextSequence}_${language}`;
};


export const validatePoi = (poi) => {
  const errors = {};
  if (!poi.name) errors.name = 'Name is required';
  if (!poi.category) errors.category = 'Category is required';
  if (!poi.coordinates.latitude || !poi.coordinates.longitude) {
    errors.coordinates = 'Coordinates are required';
  }
  return errors;
};

export const calculateDuration = (point1, point2) => {
  if (!point1 || !point2) return 0;
  
  const R = 6371; // Earth's radius in km
  const lat1 = parseFloat(point1.coordinates.latitude);
  const lon1 = parseFloat(point1.coordinates.longitude);
  const lat2 = parseFloat(point2.coordinates.latitude);
  const lon2 = parseFloat(point2.coordinates.longitude);

  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;

  const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon/2) * Math.sin(dLon/2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  const distance = R * c;

  const walkingTimeHours = distance / 5;
  return Math.round(walkingTimeHours * 60); // Convert to minutes
};

export const stripPoiAudioVariantFields = (point) => {
  if (!point || typeof point !== 'object') return point;

  const {
    uncompressedAudioUrl,
    originalAudioUrl,
    compressedAudioVariants,
    relatedGuide,
    ...rest
  } = point;

  const normalizedRelatedGuide = normalizeRelatedGuide(relatedGuide);
  const normalizedThingsToSee = normalizeThingsToSee(point.thingsToSee);
  const sanitizedPoint = {
    ...rest,
    pointType: getPointType(point),
    ...(normalizedRelatedGuide ? { relatedGuide: normalizedRelatedGuide } : {}),
    ...(normalizedThingsToSee.length > 0 ? { thingsToSee: normalizedThingsToSee } : {}),
  };

  return pruneEmptyExportFields(sanitizedPoint);
};

const pruneEmptyExportFields = (value) => {
  if (Array.isArray(value)) {
    const nextArray = value
      .map((item) => pruneEmptyExportFields(item))
      .filter((item) => !isEmptyExportValue(item));
    return nextArray;
  }

  if (value && typeof value === 'object') {
    const nextObject = Object.entries(value).reduce((acc, [key, entryValue]) => {
      const nextValue = pruneEmptyExportFields(entryValue);
      if (!isEmptyExportValue(nextValue)) {
        acc[key] = nextValue;
      }
      return acc;
    }, {});
    return nextObject;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed;
  }

  return value;
};

const isEmptyExportValue = (value) => {
  if (value == null) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value).length === 0;
  return false;
};

export const normalizeRelatedGuide = (relatedGuide) => {
  if (!relatedGuide || typeof relatedGuide !== 'object') return null;

  const guideId = String(
    relatedGuide.guideId ??
    relatedGuide.id ??
    relatedGuide.parentGuideId ??
    relatedGuide.parent_guide_id ??
    ''
  ).trim();

  if (!guideId) return null;

  return {
    guideId,
    ...(String(relatedGuide.name || '').trim()
      ? { name: String(relatedGuide.name || '').trim() }
      : {}),
    ...(String(relatedGuide.photoUrl || '').trim()
      ? { photoUrl: String(relatedGuide.photoUrl || '').trim() }
      : {}),
    ...(String(relatedGuide.website || '').trim()
      ? { website: String(relatedGuide.website || '').trim() }
      : {}),
  };
};

export const normalizeThingsToSee = (thingsToSee) => {
  if (!Array.isArray(thingsToSee)) return [];

  return thingsToSee
    .map((item) => {
      if (typeof item === 'string') {
        const label = item.trim();
        return label ? { type: 'place', label } : null;
      }

      if (!item || typeof item !== 'object') return null;

      const type = String(item.type || 'place').trim();
      const label = String(item.label || item.name || '').trim();

      if (type === 'guide') {
        const relatedGuide = normalizeRelatedGuide(item.relatedGuide || item);
        if (!relatedGuide) return null;
        return {
          type: 'guide',
          label: label || relatedGuide.name || 'Related guide',
          relatedGuide,
        };
      }

      if (!label) return null;
      return { type: 'place', label };
    })
    .filter(Boolean);
};

export const getPointType = (point) => {
  if (!point || typeof point !== 'object') return 'stop';

  const pointType = String(point.pointType || '').trim();
  if (pointType) return pointType === 'bus_stop' ? 'popup_stop' : pointType;

  return point.category === 'bus_stop' || point.category === 'popup_stop' ? 'popup_stop' : 'stop';
};

export const orderGuidePointsForExport = (points) => {
  if (!Array.isArray(points)) return [];

  const narratedStops = [];
  const busStops = [];

  points.forEach((point) => {
    if (getPointType(point) === 'popup_stop') {
      busStops.push(point);
    } else {
      narratedStops.push(point);
    }
  });

  return [...narratedStops, ...busStops];
};

export const formatItineraryForSave = (itinerary) => {
  if (itinerary.metadata.isIntroductoryStop === undefined || itinerary.metadata.isIntroductoryStop === null) {
    throw new Error(
      'Missing required field: "Introduction by Sofia" setting must be either enabled or disabled before saving.'
    );
  }

  return {
    metadata: {
      ...itinerary.metadata,
      b2bClient: Boolean(itinerary.metadata.b2bClient),
      feedbackCollect: Boolean(itinerary.metadata.feedbackCollect),
      isIntroductoryStop: Boolean(itinerary.metadata.isIntroductoryStop),
      map_display: normalizeMapDisplaySettings(itinerary.metadata.map_display),
      updatedAt: new Date().toISOString()
    },
    ...(itinerary.story_sliders?.length > 0 && { story_sliders: itinerary.story_sliders }),
    ...(itinerary.story_interests?.length > 0 && { story_interests: itinerary.story_interests }),
    points: orderGuidePointsForExport(itinerary.points).map(point => stripPoiAudioVariantFields({
      ...point,
      flexibleContent: point.flexibleContent || { title: '', content: '' },
      coordinates: {
        latitude: point.coordinates?.latitude || itinerary.metadata?.coordinates?.latitude,
        longitude: point.coordinates?.longitude || itinerary.metadata?.coordinates?.longitude
      },
      keywords: point.keywords || [],
      useSSML: point.useSSML !== undefined ? point.useSSML : true,
      nextStop: point.nextStop || {
        description: '',
        duration: '',
        transportOptions: []
      }
    }))
  };
};

export const formatItineraryFromLoad = (loadedData, currentMetadata) => {
  try {
    if (loadedData.metadata && loadedData.points) {
      const hasIntroductoryStop = loadedData.metadata.hasOwnProperty('isIntroductoryStop');
      
      if (!hasIntroductoryStop) {
        const userConfirmed = window.confirm(
          "Warning: This file doesn't contain the 'Introduction by Sofia' setting.\n\n" +
          "The checkbox will be unchecked by default. You can enable it in the Basic Information section if needed.\n\n" +
          "Click OK to continue loading the file."
        );
        if (!userConfirmed) throw new Error('File loading cancelled by user');
      }
      
      const metadata = {
        ...currentMetadata,
        ...loadedData.metadata,
        b2bClient: Boolean(loadedData.metadata.b2bClient),
        feedbackCollect: Boolean(loadedData.metadata.feedbackCollect),
        isIntroductoryStop: hasIntroductoryStop ? Boolean(loadedData.metadata.isIntroductoryStop) : false,
        map_display: normalizeMapDisplaySettings(loadedData.metadata.map_display),
        updatedAt: new Date().toISOString(),
        _hadIntroductoryStopField: hasIntroductoryStop
      };

      return {
        ...(loadedData._builder && { _builder: loadedData._builder }),
        metadata,
        points: loadedData.points.map(point => ({
          ...initialPoiState,
          ...point,
          relatedGuide: normalizeRelatedGuide(point.relatedGuide),
          pointType: getPointType(point),
          thingsToSee: normalizeThingsToSee(point.thingsToSee),
          // **[FIX]** Point IDs are preserved, new ones are generated if missing.
          id: point.id || generateId(),
          useSSML: point.useSSML !== undefined ? point.useSSML : true,
          keywords: point.keywords || [],
          coordinates: {
            latitude: point.coordinates?.latitude || metadata.coordinates?.latitude,
            longitude: point.coordinates?.longitude || metadata.coordinates?.longitude
          }
        }))
      };
    }

    if (loadedData.description && loadedData.points) {
      const metadata = {
        ...currentMetadata,
        description: loadedData.description,
        theme: loadedData.theme,
        categories: loadedData.categories || [],
        tags: loadedData.tags || [],
        duration: loadedData.duration,
        b2bClient: false,
        feedbackCollect: false,
        isIntroductoryStop: false,
        map_display: normalizeMapDisplaySettings(currentMetadata?.map_display),
        _hadIntroductoryStopField: false,
        updatedAt: new Date().toISOString()
      };

      return {
        ...(loadedData._builder && { _builder: loadedData._builder }),
        metadata,
        points: loadedData.points.map(point => ({
          ...initialPoiState,
          ...point,
          relatedGuide: normalizeRelatedGuide(point.relatedGuide),
          pointType: getPointType(point),
          thingsToSee: normalizeThingsToSee(point.thingsToSee),
          // **[FIX]** Point IDs are preserved, new ones are generated if missing.
          id: point.id || generateId(),
          useSSML: point.useSSML !== undefined ? point.useSSML : true,
          keywords: point.keywords || [],
          coordinates: {
            latitude: point.coordinates?.latitude || metadata.coordinates?.latitude,
            longitude: point.coordinates?.longitude || metadata.coordinates?.longitude
          }
        }))
      };
    }
    
    throw new Error('Invalid itinerary format');
  } catch (error) {
    console.error('Error formatting itinerary data:', error);
    if (error.message === 'File loading cancelled by user') throw error;
    throw new Error(`Failed to load itinerary from file: ${error.message}`);
  }
};

export const initialPoiState = {
  id: '',
  pointType: 'stop',
  name: '',
  description: '',
  inDepthDescription: '',
  inDepthDescriptionContext: '',
  category: '',
  tier: 'free',
  coordinates: {
    latitude: '',
    longitude: ''
  },
  estimatedDuration: '',
  audioUrl: '',
  photoUrl: '',
  photoAttribution: null,
  videoUrl: '',
  sphericalPhotoUrl: '',
  sphericalPhotoAttribution: null,
  signLanguageVideoUrl: '',
  openingHours: '',
  entryFee: '',
  tips: '',
  orientationInstructions: '',
  accessibility: '',
  bestTimeToVisit: '',
  historicalContext: '',
  culturalSignificance: '',
  hiddenGems: '',
  relatedGuide: null,
  triggerRadiusMeters: '',
  thingsToSee: [],
  flexibleContent: {
    title: '',
    content: ''
  },
  keywords: [],
  thematicConnections: [],
  visitorExperiences: [],
  historicalNarrative: '',
  useSSML: true,
  nextStop: {
    description: '',
    duration: '',
    transportOptions: []
  }
};
